import { MAX_PRODUCT_IMAGES, type ProductDetail, type TenantMedia } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  Loading,
  PageHeader,
  ProductListsCard,
  TextField,
  returnToPrevious,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { useCan } from '../api/session';
import { keys, productQuery, tenantApi } from '../api/tenant';
import { FormStatus, ImageAdder, mergeErrors, Thumbnail } from '../components/fields';
import { FlyerCard } from '../components/FlyerCard';
import { ProductForm } from '../components/ProductForm';
import { ProductLogoCard } from '../components/ProductLogoCard';
import { ContentStateBadge, ProductStatusBadge } from '../components/status';
import { StaleNotice, useVersionedDraft } from '../components/versioned';
import { he } from '../i18n/he';
import { notShownReasons, refusalOf } from '../lib/display';
import { productDraft, productUpdateRequest } from '../lib/drafts';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';
import { moveItem } from '../lib/lists';
import { galleryItems, galleryRequest } from '../lib/media';

export function ProductEditPage() {
  const { productId } = useParams({ from: '/app/products/$productId' });
  const product = useQuery({
    ...productQuery(productId),
    // While the engine writes the product's lists, the page follows until they are written.
    refetchInterval: (query) => (query.state.data?.content.writing === true ? 4000 : false),
  });
  if (product.isPending) return <Loading />;
  if (product.isError) return <Alert>{errorMessage(product.error)}</Alert>;
  return <ProductEditor key={product.data.id} product={product.data} />;
}

function factRows(product: ProductDetail) {
  const { facts } = product;
  return [
    { label: he.products.factName, value: facts.name },
    { label: he.products.factSku, value: facts.sku },
    ...(facts.barcode === null ? [] : [{ label: he.products.barcode, value: facts.barcode }]),
    ...(facts.model === null ? [] : [{ label: he.products.model, value: facts.model }]),
    ...(facts.manufacturer === null
      ? []
      : [{ label: he.products.manufacturer, value: facts.manufacturer }]),
    ...facts.specs,
  ];
}

function ProductEditor({ product }: { product: ProductDetail }) {
  const queryClient = useQueryClient();
  const canWrite = useCan('store.products.write');
  const editor = useVersionedDraft(
    product,
    (value) => value.version,
    (value) => productDraft(value),
  );
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  // Typed into since the draft was last made from the server.
  const [touched, setTouched] = useState(false);
  const change = (value: Parameters<typeof editor.set>[0]) => {
    setTouched(true);
    editor.set(value);
  };
  const resetTo = (from: ProductDetail) => {
    setTouched(false);
    editor.reset(from);
  };
  // The engine wrote the lists (no new version): an untouched form shows them.
  const wasWriting = useRef(product.content.writing);
  useEffect(() => {
    if (wasWriting.current && !product.content.writing && !touched) resetTo(product);
    wasWriting.current = product.content.writing;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the writing ends
  }, [product.content.writing]);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof tenantApi.updateProduct>[1]) =>
      tenantApi.updateProduct(product.id, body),
    onSuccess: async (updated) => {
      resetTo(updated);
      queryClient.setQueryData(keys.product(product.id), updated);
      await queryClient.invalidateQueries({ queryKey: keys.products() });
      await queryClient.invalidateQueries({ queryKey: keys.dashboard() });
    },
  });
  const reload = () => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    void queryClient
      .query({ ...productQuery(product.id), staleTime: 0 })
      // A failed read shows on the page itself (the product query's error).
      .then(resetTo, () => undefined);
  };

  const refusal = refusalOf(save.error);
  const errors = {
    ...mergeErrors(localErrors, save.error),
    ...(refusal === null ? {} : { status: refusal }),
  };

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    // The version the draft was made from, never a newer one the server returned since.
    const parsed = productUpdateRequest(editor.draft, { ...product, version: editor.baseVersion });
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };

  return (
    <>
      <Link
        to="/products"
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.products.back}
      </Link>
      <PageHeader
        title={product.content.title ?? product.facts.name}
        actions={
          <ProductStatusBadge
            status={product.status}
            hiddenFromShoppers={product.hiddenFromShoppers}
          />
        }
      />
      <div className="flex flex-col gap-6">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600">
          <span>
            {he.productOrigin[product.origin]} · {he.products.contentState}:
          </span>
          <ContentStateBadge state={product.content.state} />
        </p>
        {/* The copy of a catalog product is written by the platform; its texts stay editable here. */}
        {product.origin === 'MASTER_CATALOG' ? (
          <p className="text-xs text-slate-500" data-testid="product-copy-note">
            {he.products.copyNote}
          </p>
        ) : null}
        {product.sourceState === 'SOURCE_DISCONTINUED' ? (
          <Alert tone="warning">
            <span data-testid="product-discontinued">{he.products.sourceDiscontinued}</span>
          </Alert>
        ) : null}
        {product.publishBlockers.length === 0 ? null : (
          <Alert tone="info">
            <div data-testid="publish-blockers">
              {he.products.blockers}
              <ul className="mt-1 list-disc ps-5">
                {product.publishBlockers.map((blocker) => (
                  <li key={blocker} data-blocker={blocker}>
                    <span className="font-semibold">{he.publishBlockers[blocker]}.</span>{' '}
                    {he.publishBlockerHelp[blocker]}
                  </li>
                ))}
              </ul>
              {product.status === 'ACTIVE' ? null : (
                <p className="mt-1">{he.products.blockersNext}</p>
              )}
            </div>
          </Alert>
        )}
        {product.status === 'ACTIVE' && product.hiddenFromShoppers !== null ? (
          <Alert tone="warning">
            <span
              data-testid="product-hidden-from-shoppers"
              data-reason={product.hiddenFromShoppers}
            >
              {he.shopperHiddenHelp[product.hiddenFromShoppers]}
            </span>
          </Alert>
        ) : null}
        {notShownReasons(product).includes('READY') ? (
          <Alert tone="info">
            <span data-testid="product-ready">
              {he.products.notShownReady.replace('{status}', he.productStatus[product.status])}
            </span>
          </Alert>
        ) : null}
        {canWrite ? null : <Alert tone="info">{he.common.readOnly}</Alert>}

        <form onSubmit={onSubmit} noValidate data-testid="product-form">
          <fieldset disabled={!canWrite} className="flex flex-col gap-6">
            <StaleNotice stale={editor.stale} onReload={reload} />
            <ProductForm
              draft={editor.draft}
              set={change}
              errors={errors}
              locked={product.facts.locked}
              facts={factRows(product)}
            />
            <ProductListsCard
              words={he.productLists}
              draft={editor.draft}
              set={change}
              errors={errors}
              content={product.content}
              catalog={product.origin === 'MASTER_CATALOG'}
            />
            <FormStatus
              error={save.error}
              localInvalid={Object.keys(localErrors).length > 0}
              saved={save.isSuccess}
              // Not a stale version: reloading would not help.
              {...(refusal === null ? {} : { overrides: { CONFLICT: refusal } })}
              onReload={reload}
            />
            {canWrite ? (
              <div>
                <Button type="submit" busy={save.isPending}>
                  {he.common.save}
                </Button>
              </div>
            ) : null}
          </fieldset>
        </form>

        <Gallery product={product} canWrite={canWrite} />

        <ProductLogoCard product={product} canWrite={canWrite} />

        <FlyerCard productId={product.id} />
      </div>
    </>
  );
}

/** The product's images: order, alt texts, remove, and new uploads; saved as one replacement. */
function Gallery({ product, canWrite }: { product: ProductDetail; canWrite: boolean }) {
  const queryClient = useQueryClient();
  // Unsaved changes here survive a save of the details above (which moves the version): the
  // gallery then says the product changed, and its own save answers CONFLICT rather than
  // overwriting. With nothing edited here, it simply follows the product.
  const gallery = useVersionedDraft(
    product,
    (value) => value.version,
    (value) => ({ items: galleryItems(value.media) }),
  );
  const items = gallery.draft.items;
  const setItems = (change: (current: typeof items) => typeof items) => {
    gallery.update((draft) => ({ items: change(draft.items) }));
  };
  const addUpload = (media: TenantMedia) => {
    setItems((current) => [
      ...current,
      { key: media.id, kind: 'UPLOAD', mediaId: media.id, file: media.file, altText: '' },
    ]);
  };
  const save = useMutation({
    mutationFn: () =>
      tenantApi.replaceProductMedia(product.id, galleryRequest(items, gallery.baseVersion)),
    onSuccess: async (updated) => {
      gallery.reset(updated);
      queryClient.setQueryData(keys.product(product.id), updated);
      await queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
  const reload = () => {
    save.reset();
    void queryClient
      .query({ ...productQuery(product.id), staleTime: 0 })
      // A failed read shows on the page itself (the product query's error).
      .then(gallery.reset, () => undefined);
  };
  const full = items.length >= MAX_PRODUCT_IMAGES;

  return (
    <Card title={he.products.gallery}>
      <div className="flex flex-col gap-4" data-testid="product-gallery">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">{he.products.galleryEmpty}</p>
        ) : (
          <ol className="flex flex-col gap-3">
            {items.map((item, index) => (
              <li
                key={item.key}
                data-testid="gallery-item"
                data-kind={item.kind}
                className="flex flex-wrap items-end gap-3 rounded-md p-2 ring-1 ring-slate-200"
              >
                <Thumbnail
                  file={item.file}
                  alt={item.altText || `${he.products.image} ${String(index + 1)}`}
                />
                <TextField
                  label={`${he.products.altText} ${String(index + 1)}`}
                  value={item.altText}
                  maxLength={200}
                  disabled={!canWrite}
                  className="min-w-48 flex-1"
                  onChange={(event) => {
                    const altText = event.target.value;
                    setItems((current) =>
                      current.map((entry) =>
                        entry.key === item.key ? { ...entry, altText } : entry,
                      ),
                    );
                  }}
                />
                {canWrite ? (
                  <div className="flex gap-1">
                    <Button
                      tone="secondary"
                      aria-label={`${he.common.moveUp} ${String(index + 1)}`}
                      disabled={index === 0}
                      onClick={() => {
                        setItems((current) => moveItem(current, index, -1));
                      }}
                    >
                      ↑
                    </Button>
                    <Button
                      tone="secondary"
                      aria-label={`${he.common.moveDown} ${String(index + 1)}`}
                      disabled={index === items.length - 1}
                      onClick={() => {
                        setItems((current) => moveItem(current, index, 1));
                      }}
                    >
                      ↓
                    </Button>
                    <Button
                      tone="secondary"
                      onClick={() => {
                        setItems((current) => current.filter((entry) => entry.key !== item.key));
                      }}
                    >
                      {he.common.remove}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
        {canWrite ? (
          <>
            {full ? (
              <p className="text-xs text-slate-500">{he.products.galleryFull}</p>
            ) : (
              <ImageAdder
                label={he.products.addImage}
                onUploaded={addUpload}
                limit={MAX_PRODUCT_IMAGES - items.length}
                testId="gallery-upload"
              />
            )}
            <StaleNotice stale={gallery.stale} onReload={reload} />
            <FormStatus
              error={save.error}
              localInvalid={false}
              saved={save.isSuccess}
              onReload={reload}
            />
            <div>
              <Button
                busy={save.isPending}
                onClick={() => {
                  save.mutate();
                }}
              >
                {he.products.saveGallery}
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}
